import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Copy, Edit, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

const PromptManager = () => {
  const [copyState, setCopyState] = useState<Record<string, boolean>>({});
  const [editState, setEditState] = useState<Record<string, boolean>>({});
  const [editedPrompts, setEditedPrompts] = useState<Record<string, string>>({});
  const [savingState, setSavingState] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  // The prompt strings in this file are for display only and include template literals 
  // that would be filled in at runtime in the actual code
  type PromptDisplay = {
    id: string;
    title: string;
    description: string;
    prompt: string;
    location: string;
    tags?: string[];
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopyState({ ...copyState, [id]: true });
    setTimeout(() => {
      setCopyState({ ...copyState, [id]: false });
    }, 2000);
  };
  
  const toggleEditMode = (id: string, prompt: string) => {
    setEditState(prev => {
      const newState = { ...prev };
      newState[id] = !prev[id];
      return newState;
    });
    
    // Initialize the edited prompt with the current prompt
    if (!editedPrompts[id]) {
      setEditedPrompts(prev => ({ ...prev, [id]: prompt }));
    }
  };
  
  const updatePrompt = (id: string, value: string) => {
    setEditedPrompts(prev => ({ ...prev, [id]: value }));
  };
  
  const savePromptChanges = async (id: string, originalPrompt: string, location: string, title: string) => {
    if (editedPrompts[id] === originalPrompt) {
      toast({
        title: "No changes detected",
        description: "The prompt is unchanged.",
        variant: "default"
      });
      setEditState(prev => ({ ...prev, [id]: false }));
      return;
    }
    
    try {
      setSavingState(prev => ({ ...prev, [id]: true }));
      
      const response = await apiRequest('POST', '/api/prompts/update', {
        filePath: location,
        oldPrompt: originalPrompt,
        newPrompt: editedPrompts[id]
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast({
          title: "Prompt updated successfully",
          description: `The prompt "${title}" has been updated.`,
          variant: "default"
        });
        setEditState(prev => ({ ...prev, [id]: false }));
      } else {
        throw new Error(result.error || 'Failed to update prompt');
      }
    } catch (error: any) {
      toast({
        title: "Failed to update prompt",
        description: error.message || "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setSavingState(prev => ({ ...prev, [id]: false }));
    }
  };
  
  const cancelEdit = (id: string, originalPrompt: string) => {
    setEditedPrompts(prev => ({ ...prev, [id]: originalPrompt }));
    setEditState(prev => ({ ...prev, [id]: false }));
  };

  const PromptCard = ({ 
    title, 
    description, 
    prompt, 
    id, 
    location, 
    tags 
  }: { 
    title: string, 
    description: string, 
    prompt: string, 
    id: string, 
    location: string, 
    tags?: string[] 
  }) => (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg font-semibold">{title}</CardTitle>
            <CardDescription className="text-xs">{location}</CardDescription>
          </div>
          <div className="flex gap-2">
            {!editState[id] && (
              <>
                <button
                  onClick={() => toggleEditMode(id, prompt)}
                  className="text-xs px-2 py-1 rounded-md bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 flex items-center gap-1"
                >
                  <Edit className="h-3 w-3" />
                  Edit
                </button>
                <button
                  onClick={() => copyToClipboard(prompt, id)}
                  className="text-xs px-2 py-1 rounded-md bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" />
                  {copyState[id] ? 'Copied!' : 'Copy'}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-1 mt-1">
          {tags?.map(tag => (
            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm mb-3">{description}</p>
        {editState[id] ? (
          <Textarea
            value={editedPrompts[id] || prompt}
            onChange={(e) => updatePrompt(id, e.target.value)}
            className="min-h-[200px] font-mono text-xs"
          />
        ) : (
          <div className="bg-zinc-100 dark:bg-zinc-800 p-3 rounded-md overflow-x-auto">
            <pre className="text-xs whitespace-pre-wrap font-mono">{prompt}</pre>
          </div>
        )}
      </CardContent>
      {editState[id] && (
        <CardFooter className="flex justify-end gap-2 pt-0">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => cancelEdit(id, prompt)}
            disabled={savingState[id]}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => savePromptChanges(id, prompt, location, title)}
            disabled={savingState[id]}
          >
            {savingState[id] ? (
              <span className="flex items-center">
                <span className="animate-spin h-4 w-4 mr-2 border-2 border-b-transparent rounded-full"></span>
                Saving...
              </span>
            ) : (
              <>
                <Save className="h-4 w-4 mr-1" />
                Save Changes
              </>
            )}
          </Button>
        </CardFooter>
      )}
    </Card>
  );

  // ===== AI Orchestrator Prompts =====
  const orchestratorPrompts: PromptDisplay[] = [
    {
      id: 'orchestrator-classifier',
      title: 'Query Intent Classifier',
      description: 'Used in the AIVIA Smart orchestrator to determine which AI system should handle a user query',
      location: 'server/ai-orchestrator.ts → determineQueryIntent()',
      prompt: `You are an AI query intent classifier that determines the best system to handle a user's question. You will analyze the query and determine which of these three systems would best answer it:


1. Pinecone_RAG:  For questions requiring access to WorldVia Travel Network's proprietary knowledge, internal documentation, specific policies, procedures, or systems, as well as expert guidance on managing and growing your travel advisor business. This system is ideal for addressing inquiries that require precise, structured, and company-specific information, ensuring users receive actionable responses based on internal resources and documentation. Areas of expertise include:

WorldVia Programs, Products, and Policies:   
- Guidance on WorldVia Travel Network programs, including onboarding resources like the Launch Checklist, Ignition course, Accelerator course, and Agent Profiler workshops.
- Information about WorldVia's tools, such as the Sites website platform, CRM, Content Creator, and other apps.
- Help navigating WorldVia's supplier partnerships, including Strategic, Preferred, and Registered suppliers.

Travel Suppliers and Products:
- Recommendations for suppliers based on user and/or client needs.
- Assistance with booking procedures, commission rates, and supplier-specific policies.
- Supplier representative contact information.

Marketing and Advertising:
- Assistance with creating marketing strategies, including social media posts, blog posts, email campaigns, and newsletters.
- Assistance on using AI tools like this tool and Content Creator to generate blog posts, itineraries, and marketing copy.
- Tips for overcoming client objections and demonstrating the value of working with a travel advisor.

Business Management:
- Advice on streamlining user workflow with tools like virtual assistants, automation, and delegation.
- Help with setting and tracking KPIs (Key Performance Indicators) to measure user's business success.
- Support with client relationship management, including follow-ups, invoicing, and satisfaction tracking.

Travel Booking and Accounting Procedures:
- Assistance with booking air, cruise, and tour packages, including using tools like TRIO, Agent Air, and Travelport Cruise.
- Guidance on managing group bookings and understanding ticketing processes.
- Help with accounting procedures, including commission tracking and payment systems.

Website Development and Social Media Strategies:
- Support with building and optimizing user's website using the Sites platform, including AI-assisted content creation and SEO.
- Tips for using high-quality stock images and pre-designed templates to enhance your website.
- Assistance with creating engaging social media content and strategies.

Lead Generation and Client Acquisition:
- Help with leveraging WorldVia's lead generation programs, such as Advisor Showcase, Travel Leaders' Agent Profiler, and SEO strategies.
- Guidance on attracting and retaining clients through personalized service and marketing.

Education and Training:
- Recommendations for in-person and virtual training events, such as WORLD, Roadshows, Accelerator, Discoveries, Passport online courses, and CORE Summit.
- Support with ongoing learning through App Tuesday workshops and recorded sessions.
- Knowledge presented during WorldVia video series such as Travel Marketeering, Elevate, Luxe Discuss, Office Hours, and other shows.
- Coaching and training focused on travel suppliers, business growth, niche selection, how to get started in the travel industry, using the Launch Checklist.

Examples of inquiries best suited for this system:
- "What is the process for adding a tour component in the CRM?"
- "How do I use AIVIA to generate an itinerary?"
- "What are the steps to enroll in the Luxury Leaders Alliance program?"
- "Can you explain the difference between Strategic, Preferred, and Registered suppliers?"
- "What tools does WorldVia provide for creating marketing content?"
- "How do I optimize my website using the Sites platform?"
- "What are the guidelines for managing group bookings?"
- "How do I get a login for Royal Caribbean?"
- "Who is our Carnival rep?"
- "What commission does Disney pay?"
- "How do I cancel my account"?
- "How do I upgrade my membership?" 

This system is designed to provide comprehensive support for travel advisors, leveraging WorldVia's resources and expertise to help users succeed in their business.


2. General_LLM:  For inquiries that require general knowledge, creative tasks, opinions, advice, or questions that do not rely on proprietary company-specific information or recent updates. This system is ideal for handling broad, open-ended, or creative requests that require flexibility and general expertise. Areas of expertise include:

General Travel Knowledge: 
- Advice on popular destinations, travel tips, and general vacation planning.  
- Recommendations for activities, itineraries, and travel styles based on broad preferences.

Creative Content Generation:
- Writing professional emails, blog posts, or marketing copy that does not require company-specific branding or tools.
- Crafting engaging social media captions, taglines, or promotional content.

General Advice and Opinions:
- Offering suggestions for travel-related decisions, such as choosing between destinations or travel modes.
- Providing general insights into travel trends or best practices.

Non-Specific Travel Documentation:
- Guidance on typical travel documents, such as passports, visas, and vaccination requirements, without referencing specific company policies or procedures.

Examples of inquiries best suited for this system:
- "Help me plan a 7-day itinerary for Rome."
- "What are some good travel destinations for families with young children?"
- "Write a professional email to notify clients about our new booking system."
- "What documents are typically needed for international travel?"
- "What are some tips for packing light for a long trip?"

This system is designed to provide general, creative, and flexible responses that do not require access to proprietary company knowledge or recent updates.


3. Web_Search:  For inquiries requiring very recent information, current events, or location-specific details that are not stored in internal documentation or proprietary systems. This system is ideal for handling time-sensitive or real-time queries that depend on external sources and up-to-date data. Areas of expertise include:

Current Events and Updates:
- Information on travel advisories, restrictions, or regulations that are subject to frequent changes.
- Updates on weather forecasts, exchange rates, or flight delays.

Location-Specific Details:
- Recommendations for local attractions, restaurants, or events based on current availability.
- Real-time updates on transportation schedules, closures, or disruptions.

Dynamic and Time-Sensitive Information:
- Data that changes frequently, such as COVID-19 travel restrictions or visa requirements for specific countries.
- Information on seasonal events, festivals, or promotions.

Examples of inquiries best suited for this system:
- "What's the current exchange rate for USD to Euro?"
- "Is there a travel advisory for Thailand right now?"
- "What's the weather forecast for Paris next week?"
- "Are there any flight delays at JFK airport today?"
- "What are the COVID restrictions for travel to Japan right now?"

This system is designed to provide real-time, location-specific, and dynamic information that cannot be addressed through internal documentation or general knowledge.


Respond in JSON format with these fields:
- "tool": The system to use (Pinecone_RAG, General_LLM, or Web_Search)
- "confidence": A number between 0 and 1 indicating confidence in this decision
- "reasoning": A very brief explanation of why this system is most appropriate


Here is the user query: "[USER_QUERY]"`,
      tags: ['orchestrator', 'claude-haiku-4-5']
    },
    {
      id: 'orchestrator-web-search',
      title: 'Google Search Grounding Configuration',
      description: 'Uses Google Gemini Search Grounding for real-time web search results',
      location: 'server/ai-orchestrator.ts → performWebSearch()',
      prompt: `// Configuration for using Google Search Grounding with Gemini API
const model = gemini.getGenerativeModel({ 
  model: 'gemini-2.5-flash-preview-05-20', 
  tools: [{
    googleSearch: {}
  }]
});

// Send query with search tools enabled
const result = await model.generateContent({
  contents: [{ parts: [{ text: query }] }]
});

// The response includes groundingMetadata with real search citations`,
      tags: ['orchestrator', 'web-search', 'gemini-2.5-flash-preview-05-20']
    },
    {
      id: 'orchestrator-synthesize',
      title: 'Web Search Result Processing',
      description: 'Search results are now synthesized directly by Gemini through search grounding',
      location: 'server/ai-orchestrator.ts → synthesizeWebSearchResults()',
      prompt: `// With Google Search grounding, Gemini already synthesizes the results
// into a coherent response with sources embedded in groundingMetadata

// The synthesizeWebSearchResults function now simply returns the already
// synthesized search results:

return searchResults;`,
      tags: ['orchestrator', 'web-search', 'gemini-2.5-flash-preview-05-20']
    },
    {
      id: 'orchestrator-fallback',
      title: 'General LLM Fallback Prompt',
      description: 'Used as a fallback when web search or RAG systems fail',
      location: 'server/ai-orchestrator.ts → streamOrchestratedResponse()',
      prompt: `You are a helpful travel advisor assistant.

The user has asked: "[USER_QUERY]"

Please provide a helpful, informative response based on your general knowledge. If you don't know the answer or if the question requires very recent information, please be honest about your limitations.`,
      tags: ['orchestrator', 'fallback', 'general-knowledge']
    }
  ];

  // ===== Chat Assistant Prompts =====
  const chatPrompts: PromptDisplay[] = [
    {
      id: 'chat-system-prompt',
      title: 'AIVIA Chat System Prompt',
      description: 'The system prompt used for all AIVIA chat sessions',
      location: 'server/routes/chat.ts → createChatSession()',
      prompt: `You are a helpful travel assistant for a travel agency called WorldVia Travel. You help with travel planning, itinerary suggestions, and answering questions about destinations. Be concise, helpful, and focus on providing accurate travel information.`,
      tags: ['chat', 'system-message']
    },
    {
      id: 'chat-title-generator',
      title: 'Chat Title Generator',
      description: 'Used to generate a title for a new chat session based on the first user message',
      location: 'server/ai-chatbot.ts → generateChatTitle()',
      prompt: `Generate a concise title (maximum 6 words) for a chat conversation based on this initial user message. Respond with ONLY the title, nothing else. No quotes, period, or extra text.

Initial message: "[USER_MESSAGE]"`,
      tags: ['chat', 'title-generation', 'gemini-2.0-flash-lite']
    }
  ];

  // ===== PDF Processing Prompts =====
  const pdfPrompts: PromptDisplay[] = [
    {
      id: 'pdf-itinerary-analyzer',
      title: 'PDF Itinerary Analyzer',
      description: 'Used to analyze a travel itinerary PDF and extract key information',
      location: 'server/pdf-processor.ts → analyzeItineraryPrompt',
      prompt: `You are an expert travel document analyzer. I need you to analyze this travel itinerary text and extract two key pieces of information:

1. DESTINATION: What is the main destination or region for this trip? Provide the specific country and/or region name only.
2. DURATION: How long is this trip in days? Provide just the number of days.

Here's the travel itinerary text:
[PDF_TEXT]

Respond in JSON format with only these fields: 
{"destination": "destination name", "duration": number}`,
      tags: ['pdf-processor', 'extraction']
    },
    {
      id: 'pdf-component-extraction',
      title: 'PDF Component Extractor',
      description: 'Used to extract detailed trip components from a PDF itinerary',
      location: 'server/pdf-processor.ts → basePrompt',
      prompt: `You are an expert travel planner specialized in extracting HIGHLY SPECIFIC and DETAILED travel itinerary components from tour package documents. I've uploaded a travel package PDF that needs to be broken down into DETAILED COMPONENTS with SPECIFIC NAMES and LOCATIONS for ALL DAYS OF THE ITINERARY (approximately [DAYS_ESTIMATE] days total).

THE TRIP DATES ARE:
Start Date: [FORMATTED_START_DATE]
End Date: [FORMATTED_END_DATE]

For each component you extract, provide:
1. type: Must be one of: "hotel", "activity", "transportation", "flight", "cruise", "meal", or "freeTime"
2. name: The SPECIFIC name of the hotel, activity, etc. (e.g., "Hilton Paris Opera" not just "Hotel in Paris")
3. description: A brief description of what this component entails
4. location: The specific location (city/region and country)
5. startDay: The day number of the trip this component starts (Day 1 = [FORMATTED_START_DATE])
6. endDay: The day number this component ends (if same day, use same as startDay)
7. startTime: The time this component begins (if known, otherwise leave as null)
8. endTime: The time this component ends (if known, otherwise leave as null)
9. address: Full address if available (otherwise null)
10. notes: Any additional information like included amenities, special instructions, etc.

IMPORTANT INSTRUCTIONS:
- Create a separate component for EACH hotel stay, EACH activity, EACH meal mentioned, etc.
- Be EXTREMELY SPECIFIC with names - use the exact hotel names, tour names, etc. from the document
- Use day numbers relative to trip start date (Day 1 = [FORMATTED_START_DATE])
- If you're unsure about a start/end time, leave as null rather than guessing
- Extract ALL components from the ENTIRE itinerary, covering all days

Provide your answer as a JSON array of components, like this:
[
  {
    "type": "hotel",
    "name": "Exact Hotel Name",
    "description": "Description of the hotel stay",
    "location": "City, Country",
    "startDay": 1,
    "endDay": 3,
    "startTime": null,
    "endTime": null,
    "address": "Full address if available",
    "notes": "Any additional details"
  },
  {
    "type": "activity",
    "name": "Specific Activity Name",
    "description": "Description of the activity",
    "location": "City, Country",
    "startDay": 2,
    "endDay": 2,
    "startTime": "10:00",
    "endTime": "14:00",
    "address": null,
    "notes": "Any activity-specific details"
  }
]

Here is the PDF content to analyze:
[PDF_TEXT]`,
      tags: ['pdf-processor', 'extraction', 'components']
    },
    {
      id: 'pdf-direct-processing',
      title: 'PDF Direct Processor',
      description: 'Used to directly process a PDF file with Gemini (using the direct file API)',
      location: 'server/pdf-processor.ts → processPdfFileDirectly()',
      prompt: `You are a travel itinerary parser. I need you to extract detailed components from this \${detectedDestination} itinerary.

The trip dates are:
Start Date: \${formattedStartDate}
End Date: \${formattedEndDate}

For each component, provide these fields:
- type: Must be one of: "hotel", "activity", "transportation", "flight", "cruise", "meal", or "freeTime"
- name: The SPECIFIC name (not generic descriptions)
- description: A detailed description
- location: Specific city and country
- startDay: Day number when this component begins (Day 1 = \${formattedStartDate})
- endDay: Day number when this component ends
- startTime: Starting time if available (or null)
- endTime: Ending time if available (or null)
- address: Full address if mentioned (or null)
- notes: Additional details if available

Extract ALL components for ALL days, being extremely specific about names and details.
Respond with ONLY a JSON array of these components, with no additional text, formatted like:

[
  {
    "type": "hotel",
    "name": "Exact Hotel Name",
    "description": "4-star hotel with city views",
    "location": "Paris, France",
    "startDay": 1,
    "endDay": 3,
    "startTime": null,
    "endTime": null,
    "address": "123 Example St, Paris",
    "notes": "Includes breakfast"
  },
  {
    "type": "activity",
    "name": "Louvre Museum Private Tour",
    "description": "Guided tour of famous artworks",
    "location": "Paris, France",
    "startDay": 2,
    "endDay": 2,
    "startTime": "10:00",
    "endTime": "13:00",
    "address": "Rue de Rivoli, 75001 Paris",
    "notes": "Skip-the-line entry included"
  }
]`,
      tags: ['pdf-processor', 'extraction', 'gemini-file-api']
    }
  ];

  // ===== Trip Planning Prompts =====
  const tripPrompts: PromptDisplay[] = [
    {
      id: 'trip-generator',
      title: 'Trip Itinerary Generator',
      description: 'Used to generate a complete trip itinerary based on user specifications',
      location: 'server/ai.ts → generateItinerary()',
      prompt: `You are an expert travel planner helping a user create a detailed itinerary for their trip. 

Here are the details of the trip:
- Destination: \${destination}
- Trip Start Date: \${formatDateForPrompt(startDate)}
- Trip End Date: \${formatDateForPrompt(endDate)}
- Total Days: \${totalDays}
- Traveler Type: \${travelerType}
- Travelers: \${numAdults} adults\${numChildren > 0 ? ", \${numChildren} children" : ""}
- Budget: \${budget}
- Interests: \${interests}
- Special Requirements: \${specialRequirements || 'None'}

I need you to create a day-by-day itinerary with the following components:
1. Hotels - include exact hotel names, locations, and descriptions
2. Activities - include specific activities, attractions, tour names with times and descriptions
3. Transportation - include flights, car rentals, trains, etc. with times and details
4. Meals - include recommended restaurants for breakfast, lunch, and dinner where appropriate

For each component, provide the following information:
- Type (hotel, activity, transportation, flight, cruise, meal, or freeTime)
- Specific name (not generic descriptions)
- Detailed description
- Location (city and country)
- Day of the trip (Day 1 is \${formatDateForPrompt(startDate)})
- Start and end times (if applicable)
- Address (if applicable)
- Additional notes/details

Important guidelines:
- Be very specific with names of hotels, restaurants, and activities
- Ensure the timing is realistic and well-paced
- Consider the traveler type and interests
- Stay within the specified budget range
- Account for travel times between activities
- Include a mix of popular attractions and local experiences
- Allow for some free time in the itinerary
- Create an achievable and enjoyable schedule

Provide your response as a JSON array where each object represents a single component (hotel stay, activity, meal, etc.) of the itinerary.`,
      tags: ['trip-planning', 'itinerary-generation']
    },
    {
      id: 'trip-assistant',
      title: 'Trip Assistant Conversation',
      description: 'Used for conversational assistance with trip planning',
      location: 'server/ai.ts → tripAssistant()',
      prompt: `You are an AI travel assistant engaging in a conversation with a user about their trip itinerary.

Here are the details of the user's trip:
- Trip name: \${tripData.name}
- Destination: \${tripData.destination}
- Trip dates: \${formatDateForPrompt(startDate)} to \${formatDateForPrompt(endDate)} (\${totalDays} days)
- Traveler type: \${tripData.travelerType || 'Not specified'}
- Number of travelers: \${tripData.numAdults} adults\${tripData.numChildren > 0 ? ", \${tripData.numChildren} children" : ""}
- Budget: \${tripData.budget || 'Not specified'}
- Interests: \${tripData.interests || 'Not specified'}
- Special requirements: \${tripData.specialRequirements || 'None'}

Here's what's currently in their itinerary:
\${componentsDescription}

The user's message is: "\${message}"

Please respond to their message in a helpful, conversational way. If they're asking for changes to the itinerary, explain what changes you would recommend but don't modify the itinerary directly - they'll need to make those changes through the app interface. If they're asking for information or advice, provide detailed, helpful responses based on your travel knowledge.`,
      tags: ['trip-planning', 'conversation']
    },
    {
      id: 'trip-refiner',
      title: 'Trip Itinerary Refiner',
      description: 'Used to refine and improve a specific aspect of a trip itinerary',
      location: 'server/ai.ts → refineItinerary()',
      prompt: `You are an expert travel planner helping a user improve a specific component of their trip itinerary.

Here are the details of the trip:
- Destination: \${destination}
- Trip dates: \${formatDateForPrompt(startDate)} to \${formatDateForPrompt(endDate)} (\${totalDays} days)
- Traveler type: \${travelerType}
- Number of travelers: \${numAdults} adults\${numChildren > 0 ? ", \${numChildren} children" : ""}
- Budget: \${budget}
- Interests: \${interests}
- Special requirements: \${specialRequirements || 'None'}

The user is asking for help with refining the itinerary aspect: "\${refinementAspect}"

Here's the user's specific refinement request: "\${refinementDetails}"

Current itinerary components:
\${componentsDescription}

Please provide specific, detailed recommendations for improving this aspect of the itinerary. For each recommendation, include:
1. The specific type of component (hotel, activity, transportation, flight, cruise, meal, or freeTime)
2. Exact name (not generic descriptions)
3. Detailed description
4. Location (city and country)
5. Day of the trip it should occur (Day 1 is \${formatDateForPrompt(startDate)})
6. Start and end times (if applicable)
7. Address (if applicable)
8. Additional notes/details

Your recommendations should:
- Be very specific and detailed
- Consider the traveler type and interests
- Stay within the specified budget range
- Account for travel times between activities
- Be realistic and achievable
- Directly address the user's refinement request

Provide your response as a JSON array of component objects.`,
      tags: ['trip-planning', 'itinerary-refinement']
    },
    {
      id: 'trip-proposal',
      title: 'Trip Proposal Generator',
      description: 'Used to generate a professional proposal for a client trip',
      location: 'server/ai.ts → generateProposal()',
      prompt: `You are an expert travel advisor creating a professional proposal for a client's trip.

Here are the details of the trip:
- Trip name: \${tripData.name}
- Destination: \${tripData.destination}
- Trip dates: \${formatDateForPrompt(tripStartDate)} to \${formatDateForPrompt(tripEndDate)} (\${totalDays} days)
- Client details: \${clientDetails}
- Traveler type: \${tripData.travelerType || 'Not specified'}
- Number of travelers: \${tripData.numAdults} adults\${tripData.numChildren > 0 ? ", \${tripData.numChildren} children" : ""}
- Budget: \${tripData.budget || 'Not specified'}
- Interests: \${tripData.interests || 'Not specified'}
- Special requirements: \${tripData.specialRequirements || 'None'}

Here is the itinerary you should include in the proposal:
\${componentsDescription}

Create a comprehensive, professional travel proposal with the following sections:
1. Introduction - A personalized welcome and overview
2. Trip Summary - Brief overview of the itinerary highlighting key experiences
3. Day-by-Day Itinerary - Detailed breakdown of each day's activities, accommodations and meals
4. Accommodations - Details and highlights of the selected accommodations
5. Activities and Experiences - Featured activities and their benefits
6. Transportation - Overview of all transportation arrangements
7. Pricing Details - Clear breakdown of what's included and optional extras
8. Terms and Conditions - Cancellation policy, payment schedule, and other important information
9. Next Steps - Clear instructions for how to proceed with booking

Important guidelines:
- Write in a professional, warm, and engaging tone
- Highlight the unique and special aspects of this trip
- Address any specific interests or requirements the client mentioned
- Format the proposal in a clean, organized way using markdown
- Include specific details from the provided itinerary
- Make this proposal compelling and persuasive
- Focus on selling the experience and value, not just the features

Your proposal should be complete, detailed, and ready to present to the client.`,
      tags: ['trip-planning', 'proposal', 'client-documentation']
    }
  ];

  return (
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-bold mb-8">AI Prompt Manager</h1>
        
        <p className="mb-6 text-muted-foreground">
          This page displays all AI prompts used throughout the application for development and debugging purposes.
          Each card shows the prompt text, its location in the code, and provides a copy button for convenience.
        </p>
        
        <Tabs defaultValue="orchestrator">
          <TabsList className="mb-4">
            <TabsTrigger value="orchestrator">AIVIA Smart Orchestrator</TabsTrigger>
            <TabsTrigger value="chat">Chat Assistant</TabsTrigger>
            <TabsTrigger value="pdf">PDF Processing</TabsTrigger>
            <TabsTrigger value="trip">Trip Planning</TabsTrigger>
          </TabsList>
          
          <TabsContent value="orchestrator">
            <ScrollArea className="h-[calc(100vh-250px)]">
              <div className="pr-4">
                {orchestratorPrompts.map(prompt => (
                  <PromptCard
                    key={prompt.id}
                    id={prompt.id}
                    title={prompt.title}
                    description={prompt.description}
                    prompt={prompt.prompt}
                    location={prompt.location}
                    tags={prompt.tags}
                  />
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="chat">
            <ScrollArea className="h-[calc(100vh-250px)]">
              <div className="pr-4">
                {chatPrompts.map(prompt => (
                  <PromptCard
                    key={prompt.id}
                    id={prompt.id}
                    title={prompt.title}
                    description={prompt.description}
                    prompt={prompt.prompt}
                    location={prompt.location}
                    tags={prompt.tags}
                  />
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="pdf">
            <ScrollArea className="h-[calc(100vh-250px)]">
              <div className="pr-4">
                {pdfPrompts.map(prompt => (
                  <PromptCard
                    key={prompt.id}
                    id={prompt.id}
                    title={prompt.title}
                    description={prompt.description}
                    prompt={prompt.prompt}
                    location={prompt.location}
                    tags={prompt.tags}
                  />
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="trip">
            <ScrollArea className="h-[calc(100vh-250px)]">
              <div className="pr-4">
                {tripPrompts.map(prompt => (
                  <PromptCard
                    key={prompt.id}
                    id={prompt.id}
                    title={prompt.title}
                    description={prompt.description}
                    prompt={prompt.prompt}
                    location={prompt.location}
                    tags={prompt.tags}
                  />
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>
  );
};

export default PromptManager;
